import { useEffect, useState } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { EmailVerification } from "@/components/email-verification"
import { GoogleAuthButton } from "@/components/google-auth-button"
import { Button } from "@/components/ui/button"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { authClient } from "@/lib/auth-client"
import { parseVideoSourceSearch } from "@/lib/video-source"

export const Route = createFileRoute("/_auth/signin")({
  validateSearch: parseVideoSourceSearch,
  head: () => ({
    meta: [
      { title: "Sign in — intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SignInPage,
})

function SignInPage() {
  const navigate = useNavigate()
  const { source } = Route.useSearch()
  const { data: session } = authClient.useSession()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null
  )
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (session)
      void navigate({
        to: "/app",
        search: source ? { source } : {},
        replace: true,
        reloadDocument: true,
      })
  }, [navigate, session, source])

  async function signIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    try {
      const result = await authClient.signIn.email({
        email: email.trim(),
        password,
      })
      if (result.error?.code === "EMAIL_NOT_VERIFIED") {
        const normalizedEmail = email.trim().toLowerCase()
        const delivery = await authClient.emailOtp.sendVerificationOtp({
          email: normalizedEmail,
          type: "email-verification",
        })
        if (delivery.error) {
          setError(
            delivery.error.code === "INVALID_EMAIL"
              ? "This email address is invalid."
              : "Unable to send your verification code. Wait a minute and sign in again."
          )
          return
        }
        setPassword("")
        setVerificationEmail(normalizedEmail)
        return
      }
      if (result.error) {
        setError(
          result.error.status === 401
            ? "Email or password is incorrect. Please try again."
            : "Unable to sign in. Please try again."
        )
        return
      }
      await navigate({
        to: "/app",
        search: source ? { source } : {},
        replace: true,
        reloadDocument: true,
      })
    } catch {
      setError(
        "Unable to reach the server. Check your connection and try again."
      )
    } finally {
      setPending(false)
    }
  }

  if (verificationEmail) {
    return (
      <main className="auth-main">
        <EmailVerification
          email={verificationEmail}
          source={source}
          onBack={() => {
            setVerificationEmail(null)
            setError(null)
          }}
        />
      </main>
    )
  }

  return (
    <main className="auth-main">
      <section className="auth-panel" aria-labelledby="sign-in-title">
        <p className="auth-kicker">Welcome back</p>
        <h1 id="sign-in-title">Sign in to intoshort</h1>
        <p className="auth-description">
          Continue with Google or use your email and password.
        </p>
        <div className="auth-social">
          <GoogleAuthButton
            disabled={pending}
            onError={setError}
            callbackPath={
              source ? `/app?source=${encodeURIComponent(source)}` : "/app"
            }
          />
          <div className="auth-divider">
            <Separator aria-hidden="true" />
            <span>or</span>
            <Separator aria-hidden="true" />
          </div>
        </div>
        <form onSubmit={signIn} aria-busy={pending}>
          <FieldGroup>
            <Field data-disabled={pending}>
              <FieldLabel htmlFor="sign-in-email">Email</FieldLabel>
              <Input
                id="sign-in-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={pending}
              />
            </Field>
            <Field data-disabled={pending}>
              <FieldLabel htmlFor="sign-in-password">Password</FieldLabel>
              <Input
                id="sign-in-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={pending}
              />
            </Field>
            {error && (
              <p className="auth-error" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" size="lg" disabled={pending}>
              {pending ? "Signing in…" : "Sign in"}
            </Button>
          </FieldGroup>
        </form>
        <p className="auth-switch">
          New to intoshort?{" "}
          <Link to="/signup" search={source ? { source } : {}} reloadDocument>
            Create an account
          </Link>
          .
        </p>
      </section>
    </main>
  )
}
